import React, { useContext, useMemo } from 'react';
import {
    Divider,
    Grid,
    TextField,
    makeStyles,
    Typography,
} from '@material-ui/core';
import { useTranslation } from "react-i18next";
import { getErrorMessage, isInputError } from 'helpers';
import Alert from '@material-ui/lab/Alert';
import { getCsvMappingFields, isBasicShipperVersion, isWeightRequired } from '../../../config/shipperPackageFormConfig';
import AppContext from '../../../context/AppContext';

const useStyles = makeStyles((theme) => ({
    input: {
        maxWidth: '226px'
    },
}));

const labelKey = (name) => (name === 'reference_number' ? name : name.replace('receiver_', ''));

const PackagesStep = ({ packages, setPackages, shipper, shipperShortcut, ...rest }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { appValues } = useContext(AppContext);
    const shortcut = shipperShortcut ?? shipper;
    const isBasicVersion = isBasicShipperVersion(appValues, { shortcut });
    const editableFields = useMemo(() => {
        const { base, additional } = getCsvMappingFields(shortcut, { isBasicVersion });
        return [...base, ...additional]
            .map((f) => f.name)
            .filter((name) => name !== 'reference_number');
    }, [isBasicVersion, shortcut]);
    const disabledFieldsForParcelshops = ['receiver_company', 'receiver_street', 'receiver_house_number', 'receiver_zip', 'receiver_city', 'receiver_state_code', 'count_of_packages']
    const weightRequired = useMemo(() => isWeightRequired(shortcut, { isBasicVersion }), [isBasicVersion, shortcut]);

    const getOtherErrors = (pErrors, getArray = false) => {
        let errors = [];
        for (const key in pErrors) {
            if (key.includes('receiver') || ['insurance', 'cod_reference', 'cod_price', 'count_of_packages'].includes(key) || pErrors[key].length === 0 ) {
                continue;
            }
            if (typeof pErrors[key] === 'object') {
                let tmpRes = getOtherErrors(pErrors[key], true);
                if (tmpRes.length !== 0)
                    errors = errors.concat(t('label.'+key) + " : " + tmpRes.join(','));
            } else {
                errors.push(pErrors[key]);
            }
        }
        if (getArray) {
            return errors;
        }
        return errors.length ? errors.join(', ') : null;
    };

    const onChange = (e, index) => {
        const { name, value } = e.target;
        if (name.startsWith('others_')) {
            const othersKey = name.substring(7);
            if (!packages[index].package.others) {
                packages[index].package.others = {};
            }
            packages[index].package.others[othersKey] = value;
        } else {
            packages[index].package[name] = value;
        }
        if (name === 'weight' && weightRequired) {
            const errors = { ...(packages[index].errors || {}) };
            const provided = value !== '' && value !== null && value !== undefined;
            if (provided && !(Number(value) > 0)) {
                errors.weight = [t('messages.weight_must_be_positive')];
            } else {
                delete errors.weight;
            }
            packages[index].errors = errors;
        }
        setPackages([...packages]);
    };

    const isDisabled = (p, field) => {
        if (shipper.toUpperCase() === 'PACKETA' && field === 'count_of_packages'){
            return disabledFieldsForParcelshops.includes(field);
        } else {
            return !!p.parcelshop && disabledFieldsForParcelshops.includes(field);
        }
    };

    const getFieldValue = (pack, fieldName) => {
        if (fieldName.startsWith('others_')) {
            const othersKey = fieldName.substring(7);
            return pack.package.others?.[othersKey] ?? '';
        }
        return pack.package[fieldName] ?? (fieldName === 'count_of_packages' ? 1 : '');
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                {
                    packages.map((pack, index) => 
                        <Grid container spacing={2} key={index}>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    {index + 1}. {pack.package['reference_number']}
                                </Typography>
                            </Grid>
                            {editableFields.map((field, i) => (
                                <Grid item xs={'auto'} key={i}>
                                    <TextField
                                        label={t('label.' + labelKey(field))}
                                        value={getFieldValue(pack, field)}
                                        variant="outlined"
                                        size="small"
                                        onChange={(e) => onChange(e, index)}
                                        name={field}
                                        error={isInputError(pack.errors, field)}
                                        helperText={getErrorMessage(pack.errors, field)}
                                        className={classes.input}
                                        disabled={isDisabled(pack.package, field)}
                                    />
                                </Grid>
                            ))}
                            {
                                getOtherErrors(pack.errors) &&
                                <Grid item xs={12}>
                                    <Alert variant="filled" severity="error">
                                        {getOtherErrors(pack.errors) }
                                    </Alert>
                                </Grid>
                            }
                            <Grid item xs={12}>
                                <Divider/>
                            </Grid>
                        </Grid>
                    )
                }
            </Grid>
        </Grid>
    );
};

export default PackagesStep;
